import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { Unit } from 'src/features/units/utils/types';
import { useDeleteUnit } from 'src/features/units/api/useDeleteUnit';
import { LIST_UNITS_RESOURCE } from 'src/features/units/api/useListUnits';
import { unitsMessages } from 'src/features/units/utils/messages';

interface DeleteUnitFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedUnit: Unit;
    children?: React.ReactNode;
}

function DeleteUnitFormModal<TData>({ selectedUnit, children }: DeleteUnitFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: deleteUnit } = useDeleteUnit({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_UNITS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(unitsMessages.deleteUnit),
                description: formatMessage(unitsMessages.deleteUnitSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...unitsMessages.deleteUnit },
                subTitle: {
                    ...unitsMessages.deleteUnitSubtitle,
                    values: {
                        entity: selectedUnit.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...unitsMessages.confirmDeleteUnit },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: unitsMessages.deleteUnit,
                callbackFn: () => {
                    return (
                        deleteUnit({
                            unitId: selectedUnit.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteUnitFormModal;
